import { Button, Group, Menu, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconCopy } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { Podatki } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { imeOsebe, naslov, VRSTE, type Vrsta } from './vrste';

type Oseba = Shema['OsebaPodatki'];

/**
 * »Kopiraj v kupce / prodajalce / uporabnike vozil«: če v ciljni vrsti že obstaja oseba z enakim imenom in priimkom, vpraša, ali je ista (takrat
 * jo posodobi), sicer doda novo. Kopira strežnik (osnovni podatki in kontakti).
 */
export function KopirajOsebo({ vrsta, id }: { vrsta: Vrsta; id: number }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [cilj, setCilj] = useState<Vrsta | null>(null);
  const [kandidat, setKandidat] = useState<Oseba | null>(null);
  const [dela, setDela] = useState(false);
  const path = { vrsta: vrsta.kljuc, id };

  const kopiraj = async (c: Vrsta, posodobi?: number) => {
    setDela(true);
    try {
      const o = await podatki(
        api.POST('/api/osebe/{vrsta}/{id}/kopija', { params: { path, query: { cilj: c.kljuc, posodobi } } }),
      );
      setKandidat(null);
      setCilj(null);
      await odjemalec.invalidateQueries({ queryKey: ['osebe'] });
      await odjemalec.invalidateQueries({ queryKey: ['osebe-stevilo'] });
      notifications.show({
        color: 'green',
        message: (
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <span>{posodobi ? `${c.ednina} je posodobljen.` : `Dodan ${c.ednina.toLowerCase()}.`}</span>
            <Button size="compact-xs" variant="light" onClick={() => void navigate(`/osebe/${c.kljuc}/${o.id}`)}>
              Odpri
            </Button>
          </Group>
        ),
      });
    } catch (e) {
      notifications.show({ color: 'red', message: sporocilo(e) });
    } finally {
      setDela(false);
    }
  };

  const zacni = async (c: Vrsta) => {
    setDela(true);
    try {
      const istoimenske = await podatki(
        api.GET('/api/osebe/{vrsta}/{id}/kopija', { params: { path, query: { cilj: c.kljuc } } }),
      );
      if (istoimenske.length > 0) {
        setCilj(c);
        setKandidat(istoimenske[0]!);
        setDela(false);
        return;
      }
      setDela(false);
      await kopiraj(c);
    } catch (e) {
      setDela(false);
      notifications.show({ color: 'red', message: sporocilo(e) });
    }
  };

  return (
    <>
      <Menu position="bottom-end" shadow="md" radius="md">
        <Menu.Target>
          <Button
            variant="default"
            leftSection={<IconCopy size={18} />}
            rightSection={<IconChevronDown size={14} />}
            loading={dela && !kandidat}
          >
            Kopiraj v
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {VRSTE.filter((c) => c.kljuc !== vrsta.kljuc).map((c) => (
            <Menu.Item key={c.kljuc} onClick={() => void zacni(c)}>
              {c.naziv}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      <PomocIkona clanek="kopiranje-osebe" />
      <Modal
        opened={kandidat !== null}
        onClose={() => !dela && setKandidat(null)}
        title={`${cilj?.ednina ?? ''} s podobnimi podatki že obstaja`}
        radius="lg"
      >
        {kandidat && cilj && (
          <Stack gap="sm">
            <Text size="sm">Ali je to ista oseba? Če je, se njeni podatki posodobijo, sicer se doda nova.</Text>
            <Podatki
              vrstice={[
                ['Ime', imeOsebe(kandidat)],
                ['Davčna številka', kandidat.davcna ?? ''],
                ['EMŠO', kandidat.emso ?? ''],
                ['Naslov', naslov(kandidat)],
              ]}
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => setKandidat(null)} disabled={dela}>
                Prekliči
              </Button>
              <Button variant="default" onClick={() => void kopiraj(cilj)} loading={dela}>
                Ne, dodaj novo
              </Button>
              <Button onClick={() => void kopiraj(cilj, kandidat.id)} loading={dela}>
                Da, posodobi
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
